import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiFetch } from './config';
import { useAuth } from './AuthContext';
import Nav from './Nav';
import EdgeSentence from './EdgeSentence';
import { Explain } from './Explain';
import { TrustBar, LensChips, fmtScore, scoreColor, relationColor } from './trustShared';
import './css/trust.css';

import { snip as snipAt } from './snip';
const snip = (s, n = 80) => snipAt(s, n);

function CompareView() {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const ids = (searchParams.get('ids') || '').split(',').filter(Boolean);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCompare = useCallback(async (withSpinner) => {
    try {
      if (withSpinner) setLoading(true);
      const response = await apiFetch(`/api/claims/compare?ids=${ids.join(',')}`);
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to compare claims');
      }
      setData(await response.json());
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching comparison:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (ids.length >= 2) fetchCompare(true);
    else { setError('Provide between 2 and 4 claims to compare'); setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchCompare]);

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;
  if (error) return <div style={{ padding: '20px', color: 'var(--wv-neg)' }}>Error: {error}</div>;
  if (!data) return null;

  const { claims, lenses, crossEdges, sharedNeighbors } = data;
  const claimById = Object.fromEntries(claims.map((c) => [c.id, c]));
  const withoutUrl = (removeId) => `/compare?ids=${ids.filter((x) => x !== removeId).join(',')}`;

  return (
    <div className="trust-page">
      <Nav />
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 20px 40px' }}>
        <h1 style={{ marginTop: 0 }}>Compare claims</h1>

        {/* Side-by-side columns */}
        <div style={{ display: 'flex', gap: '14px', alignItems: 'stretch', flexWrap: 'wrap' }}>
          {claims.map((c) => (
            <div key={c.id} className="trust-section" style={{ flex: '1 1 230px', minWidth: '230px', display: 'flex', flexDirection: 'column' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                <Explain concept="combined-score">
                  <strong style={{ color: scoreColor(c.combined), fontSize: '20px' }}>{fmtScore(c.combined)}</strong>
                </Explain>
                {ids.length > 2 && (
                  <Link to={withoutUrl(c.id)} className="trust-muted" style={{ fontSize: '12px' }}>remove ×</Link>
                )}
              </div>
              <TrustBar score={c.combined} width={200} />
              <div style={{ margin: '12px 0 8px', fontWeight: 500, lineHeight: 1.45, flex: 1 }}>
                <Link to={`/object/${c.id}`} style={{ color: 'inherit' }}>{c.content}</Link>
                {c.conflict && (
                  <Explain concept="contested"><span style={{ color: 'var(--wv-warn)', marginLeft: '8px' }}>⚡</span></Explain>
                )}
              </div>
              <div className="trust-muted" style={{ fontSize: '12px', marginBottom: '10px' }}>
                by {c.creator_username || 'unknown'} · {c.object_type} ·{' '}
                <Explain concept="voting">{c.vote_total} vote{c.vote_total !== 1 ? 's' : ''} on its edges</Explain>
              </div>
              <LensChips lenses={lenses} scores={c.scores} size="small" />
              <div style={{ display: 'flex', gap: '12px', marginTop: '12px', fontSize: '12px', flexWrap: 'wrap' }}>
                <Explain concept="support-degree">
                  <span style={{ color: relationColor('supports') }}>
                    ▲ {c.supports_in} in / {c.supports_out} out
                  </span>
                </Explain>
                <span style={{ color: relationColor('contradicts') }}>
                  ▼ {c.contradicts_in} in / {c.contradicts_out} out
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Direct relations between the compared claims */}
        <h2 style={{ marginTop: '34px' }}>
          <Explain concept="relation">Direct relations between these claims</Explain>
        </h2>
        {crossEdges.length === 0 ? (
          <p className="trust-muted">These claims are not directly linked. If they should be, link them from an object page.</p>
        ) : (
          crossEdges.map((e) => (
            <EdgeSentence
              key={e.id}
              edge={{
                ...e,
                source_id: e.source,
                source_content: claimById[e.source]?.content || '?',
                target_id: e.target,
                target_content: claimById[e.target]?.content || '?',
              }}
              onVoted={() => fetchCompare(false)}
              user={user}
            />
          ))
        )}

        {/* Shared neighborhood */}
        <h2 style={{ marginTop: '34px' }}>
          <Explain concept="shared-neighborhood">Shared neighborhood</Explain>
        </h2>
        {sharedNeighbors.length === 0 ? (
          <p className="trust-muted">No objects relate to two or more of these claims.</p>
        ) : (
          sharedNeighbors.map(({ node, perClaim }) => (
            <div key={node.id} className="trust-row" style={{ cursor: 'default' }}>
              <strong style={{ color: scoreColor(node.combined), minWidth: '52px', textAlign: 'right' }}>
                {fmtScore(node.combined)}
              </strong>
              <div style={{ flex: 1 }}>
                <Link to={`/object/${node.id}`} style={{ color: 'inherit' }}>{node.content}</Link>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '8px' }}>
                  {claims.filter((c) => perClaim[c.id]).map((c) => (
                    <div key={c.id} style={{ fontSize: '12px' }}>
                      <span className="trust-muted">vs “{snip(c.content, 46)}”: </span>
                      {perClaim[c.id].map((e) => (
                        <span
                          key={e.id}
                          className="relation-pill"
                          style={{ backgroundColor: relationColor(e.relation_name) }}
                        >
                          {e.source === c.id ? '→' : '←'} {e.relation_name}{e.polarity === -1 ? ' (neg)' : ''}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default CompareView;
